import { Accordion, Flex, VStack } from "@chakra-ui/react";
import { offeringIsTerminated } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { BannerAlert, Heading, Icon, Paper, Text } from "@equidefi/ui";
import { usePlaidEnabled } from "hooks/usePlaid";
import { PlaidUploadArea } from "./PlaidUploadArea";
import { ProofOfAddress } from "./ProofOfAddress";
import UploadArea from "./UploadArea";

type StepIconProps = {
  completed: boolean;
  text: string;
};

export const StepIcon = ({ completed, text }: StepIconProps) => {
  const CustomIcon = completed ? Icon.Check : Icon.X;
  const color = completed ? "green" : "red";

  return (
    <Flex align="center" gap="5px">
      <CustomIcon size="2em" color={color} />
      <Text mb="0">{text}</Text>
    </Flex>
  );
};

type UploadSectionProps = {
  vault: DTO.Vault;
  investment: DTO.Investment;
  offering: DTO.Offering;
  accreditationFiles: DTO.AccreditationFile[];
  refetch: () => Promise<void>;
};

export const AccreditationUploadSection = (props: UploadSectionProps) => {
  const { offering, investment, vault, accreditationFiles, refetch } = props;

  const isOfferingTerminated = offeringIsTerminated(offering);

  const plaidEnabled = usePlaidEnabled(offering);

  return (
    <>
      <Paper my={4} p={4}>
        <VStack align="start">
          <StepIcon completed={!!vault?.utility_bill} text="Upload Proof of Address" />

          <StepIcon completed={!!accreditationFiles?.length} text="Verify Your Accreditation" />
        </VStack>
      </Paper>

      {isOfferingTerminated ? (
        <BannerAlert
          status="error"
          my={4}
          description={
            <Text textStyle="body2" m={0}>
              This offering has been terminated. Document uploads and modifications are not allowed.
            </Text>
          }
        />
      ) : (
        <BannerAlert
          status="info"
          my={4}
          description={
            <Text textStyle="body2" m={0}>
              Your investment won't be submitted until you click "Submit Investment" at the bottom of this page.
            </Text>
          }
        />
      )}

      <ProofOfAddress
        isTerminated={isOfferingTerminated}
        vault={vault}
        investmentId={investment?.id}
        refetch={refetch}
      />

      <Paper my={4} p={4}>
        <Heading fontWeight="bold">Verification Method</Heading>
        <Text mb="4">You must upload documents that verify your status as an accredited investor.</Text>

        <Accordion variant="card" defaultIndex={-1}>
          {plaidEnabled && (
            <PlaidUploadArea
              isTerminated={isOfferingTerminated}
              refetch={() => {
                refetch();
              }}
              files={accreditationFiles}
              vault={vault}
              offering={offering}
            />
          )}

          <UploadArea
            accreditationFiles={accreditationFiles}
            vault={vault}
            investment={investment}
            refetchFiles={refetch}
            isTerminated={isOfferingTerminated}
          />
        </Accordion>
      </Paper>
    </>
  );
};
